<ion-content>
  <div class="warning-feature-banner" *ngIf="isSlackDisconnected">
    <ion-icon name="warning" class="padded-icon"></ion-icon>
    The Unthread app for Slack appears to be disconnected
    <ion-button color="dark" class="btn-small" routerLink="/dashboard/settings/slack" routerDirection="root">
      <ion-icon name="arrow-up-circle" class="padded-icon"></ion-icon>
      Visit Slack settings to reinstall
    </ion-button>
  </div>

  <div class="constrained-content">
    <ion-grid>
      <ion-row class="row">
        <ion-col size-md="5" size="12" class="settings-title-section">
          <h1><ion-icon name="swap-horizontal-outline"></ion-icon>Triage Channel</h1>
          <p class="subtitle">Choose an internal channel to cross-post conversations to.</p>
        </ion-col>
        <ion-col size-md="7" size="12">
          <ion-card class="outline-card">
            <ion-card-content>
              <app-channel-selector
                [channel]="triageChannel"
                (channelDidChange)="onTriageChannelChange($event)"
                [defaultInternalOnly]="true"
              ></app-channel-selector>
            </ion-card-content>
          </ion-card>
          <a href="https://docs.unthread.io/conversations/triaging" target="_blank" class="settings-learn-more">
            <ion-icon name="book-outline"></ion-icon>
            Learn more about the triage channel
          </a>
        </ion-col>
      </ion-row>

      <ion-row class="row">
        <ion-col size-md="5" size="12" class="settings-title-section">
          <h1><ion-icon name="arrow-undo-circle-outline"></ion-icon>Auto-Responder</h1>
          <p class="subtitle">Automatically reply in a thread to new messages.</p>
        </ion-col>
        <ion-col size-md="7" size="12">
          <ion-card class="outline-card">
            <app-autoresponder
              [isDefault]="true"
              [initialAutoresponder]="autoresponder"
              (autoresponderDidChange)="autoresponderDidChange($event)"
            ></app-autoresponder>
          </ion-card>
          <a href="https://docs.unthread.io/engagement/autoresponder" target="_blank" class="settings-learn-more">
            <ion-icon name="book-outline"></ion-icon>
            Learn more about the auto-responder
          </a>
        </ion-col>
      </ion-row>

      <ion-row class="row">
        <ion-col size-md="5" size="12" class="settings-title-section">
          <h1><ion-icon name="albums-outline"></ion-icon>Customer-Facing Ticket View</h1>
          <p class="subtitle">Give customers a view to track ticket statuses.</p>
        </ion-col>
        <ion-col size-md="7" size="12">
          <ion-card class="outline-card">
            <ion-list>
              <ion-item>
                <ion-label text-wrap>
                  <h3>Enable Customer-Facing Ticket View in all customer Slack channels</h3>
                  <p>Pins a button in channels for customers to view support requests they have submitted.</p>
                </ion-label>
                <label class="switch" slot="end">
                  <input type="checkbox" [(ngModel)]="customerViewEnabled" />
                  <span class="slider"></span>
                </label>
              </ion-item>
              <ng-container *ngIf="customerViewEnabled">
                <ion-item>
                  <ion-label text-wrap>
                    <p>
                      <ion-icon name="checkmark"></ion-icon> Enabling this will post a message to all customer channels
                      with a button to view requests
                    </p>
                    <p>
                      <ion-icon name="checkmark"></ion-icon> This message will be pinned so it is easily accessible from
                      the channel
                    </p>
                    <p>
                      <ion-icon name="checkmark"></ion-icon> You can also enable this on a per-customer basis to try it
                      out
                    </p>
                  </ion-label>
                </ion-item>
                <ion-item>
                  <ion-label text-wrap>
                    <h3>Enable Private mode for Customer-Facing Ticket View in all customer Slack channels</h3>
                    <p>
                      New tickets will not be shared to the channel, and users will only be able to see their own
                      outstanding tickets.
                    </p>
                  </ion-label>
                  <label class="switch" slot="end">
                    <input type="checkbox" [(ngModel)]="customerViewSettings.isPrivate" />
                    <span class="slider"></span>
                  </label>
                </ion-item>
                <ion-item *ngIf="customerViewSettings.isPrivate">
                  <ion-label text-wrap>
                    <p><ion-icon name="checkmark"></ion-icon> You can override this setting on a per-customer basis</p>
                  </ion-label>
                </ion-item>
                <ion-item *ngIf="customerViewSettings">
                  <ion-label text-wrap>
                    <h3>Pin to bottom</h3>
                    <p>Pins the customer request tracker to the bottom of the channel</p>
                  </ion-label>
                  <label class="switch" slot="end">
                    <input type="checkbox" [(ngModel)]="customerViewSettings.isPinnedToBottom" />
                    <span class="slider"></span>
                  </label>
                </ion-item>
                <ion-item *ngIf="customerViewSettings">
                  <ion-label text-wrap>
                    <h3>Custom Message</h3>
                    <p>Add a custom message that will be displayed to customers (optional)</p>
                  </ion-label>
                </ion-item>
                <div class="text-editor-container">
                  <app-slack-text-editor
                    [content]="customerViewSettings.customMessage"
                    placeholder="Your custom message here..."
                    style="width: 100%"
                    [hideStrikethrough]="true"
                    [hideCodeblock]="true"
                    [hideCode]="true"
                    (contentDidChange)="onMessageContentChange($event)"
                  >
                  </app-slack-text-editor>
                </div>
                <ion-item *ngIf="customerViewSettings">
                  <ion-label text-wrap>
                    <h3>Custom Create Conversation Modal Name</h3>
                    <p>
                      Add a custom name to the create conversation modal. By default, it will be "Create Conversation"
                      (optional)
                    </p>
                  </ion-label>
                </ion-item>
                <ion-input
                  class="full-width-input"
                  [(ngModel)]="customerViewSettings.customCreateConversationModalName"
                  placeholder="Create Conversation"
                  maxlength="25"
                ></ion-input>
              </ng-container>
            </ion-list>
          </ion-card>
          <a href="https://docs.unthread.io/engagement/request-history" target="_blank" class="settings-learn-more">
            <ion-icon name="book-outline"></ion-icon>
            Learn more about the customer-facing ticket view
          </a>
        </ion-col>
      </ion-row>

      <ion-row class="row">
        <ion-col size-md="5" size="12" class="settings-title-section">
          <h1><ion-icon name="calendar-outline"></ion-icon>Support Team & Scheduling</h1>
          <p class="subtitle">Set the appearance and working hours of your team.</p>
        </ion-col>
        <ion-col size-md="7" size="12">
          <ion-card class="outline-card">
            <ion-list>
              <ion-item class="no-padding">
                <strong>Support name & logo</strong>
              </ion-item>
            </ion-list>
            <div class="notification-preview">
              <div
                class="notification-icon"
                style="width: 50px; height: 50px; border-radius: 8px; border: 1px solid var(--input-border-color)"
              >
                <ion-button
                  slot="end"
                  size="full"
                  fill="clear"
                  (click)="uploadSupportLogo()"
                  class="icon-upload-button"
                >
                  <ion-icon name="cloud-upload-outline" [ngClass]="{ hide: !!supportLogo }"></ion-icon>
                </ion-button>
                <img *ngIf="supportLogo" [src]="supportLogo" />
              </div>
              <div>
                <ion-input
                  placeholder="Unthread"
                  [(ngModel)]="supportName"
                  class="select-outline"
                  style="margin-left: 5px; height: 50px; font-size: 1.1rem"
                >
                </ion-input>
              </div>
            </div>

            <ion-list>
              <ion-item>
                <ion-label>Support email address</ion-label>
                <ion-input slot="end" [(ngModel)]="email" placeholder="support@piedpiper.com"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label>Working hours ({{ workingHoursEnabled ? 'enabled' : 'disabled' }})</ion-label>
                <ion-button color="dark" fill="clear" class="button-dark-border" (click)="manageWorkingHours()">
                  Manage working hours
                </ion-button>
              </ion-item>
            </ion-list>
          </ion-card>
        </ion-col>
      </ion-row>

      <ion-row class="row">
        <ion-col size-md="5" size="12" class="settings-title-section">
          <h1><ion-icon name="person-circle-outline"></ion-icon>Custom User Attributes</h1>
          <p class="subtitle">Define custom fields to store additional information about users.</p>
        </ion-col>
        <ion-col size-md="7" size="12">
          <ion-card class="outline-card">
            <app-custom-fields
              placeholder="Ex: Department"
              [customFields]="users.customProfileFields"
              [addCustomFieldButtonLabel]="'Add a custom user attribute'"
              [showPublicOption]="true"
              [showUserAttributeOption]="true"
              (didChange)="customUserFieldsDidChange()"
            >
            </app-custom-fields>
          </ion-card>
          <!-- TODO: Add learn more link if applicable -->
          <!-- <a href="https://docs.unthread.io/..." target="_blank" class="settings-learn-more">
            <ion-icon name="book-outline"></ion-icon>
            Learn more about custom user attributes
          </a> -->
        </ion-col>
      </ion-row>

      <ion-row class="row">
        <ion-col size-md="5" size="12" class="settings-title-section">
          <h1><ion-icon name="chatbox-ellipses-outline"></ion-icon>Conversation Settings</h1>
          <p class="subtitle">Customize how conversation statuses are updated.</p>
        </ion-col>
        <ion-col size-md="7" size="12">
          <ion-card class="outline-card">
            <ion-card-title class="card-title">
              <h3>General Conversation Settings</h3>
            </ion-card-title>
            <ion-list>
              <ion-item>
                <ion-label>
                  <h3>New replies move conversation status to "Needs response"</h3>
                  <p>Default behavior is to leave conversation as "In progress".</p>
                </ion-label>
                <label class="switch" slot="end">
                  <input type="checkbox" [(ngModel)]="moveToNeedsResponse" />
                  <span class="slider"></span>
                </label>
              </ion-item>
              <ion-item>
                <ion-label text-wrap>
                  <h3>Require "<code>/unthread send</code>" in Slack for external replies</h3>
                  <p>
                    If enabled, you will need to append <code>/unthread send</code> to send replies via Email, Teams, or
                    In-app chat when replying from Slack (replying from dashboard always sends by default). Messages
                    without <code>/unthread send</code> will be internal notes.
                  </p>
                </ion-label>
                <label class="switch" slot="end">
                  <input type="checkbox" [(ngModel)]="requireUnthreadSend" />
                  <span class="slider"></span>
                </label>
              </ion-item>
              <ion-item>
                <ion-label>
                  <h3>Send post-close message</h3>
                  <p>Automatically send a message or survey after closing</p>
                </ion-label>
                <label class="switch" slot="end">
                  <input type="checkbox" [(ngModel)]="postCloseTemplateEnabled" />
                  <span class="slider"></span>
                </label>
              </ion-item>
              <ng-container *ngIf="postCloseTemplateEnabled">
                <ion-item>
                  <ion-label slot="end">
                    Choose a template
                    <a routerLink="/dashboard/outbound/templates" routerDirection="root">(manage templates)</a>
                  </ion-label>
                  <ion-label slot="end">
                    <ion-chip
                      mode="ios"
                      class="select-chip select-outline"
                      [outline]="true"
                      (click)="choosePostCloseTemplate()"
                    >
                      <ion-label>
                        {{ postCloseTemplateId ? postCloseTemplate?.name : 'None' }}
                        <ion-icon name="caret-down-outline" class="icon-padded-left"></ion-icon>
                      </ion-label>
                    </ion-chip>
                  </ion-label>
                </ion-item>
                <ion-item>
                  <ion-label slot="end" text-wrap>
                    Limit to ticket types:
                    <ion-chip
                      mode="ios"
                      color="primary"
                      class="btn-small tag-chip"
                      slot="end"
                      *ngFor="let ticket of postCloseTicketTypes; let i = index"
                      [title]="ticket.name"
                      (click)="removeTicketType(i)"
                    >
                      <ion-label>
                        {{ ticket.name }}
                        <ion-icon name="close-outline"></ion-icon>
                      </ion-label>
                    </ion-chip>
                  </ion-label>
                  <ion-button
                    class="btn-small"
                    fill="clear"
                    style="--padding-start: 4px; margin: 5px; --padding-end: 4px"
                    slot="end"
                    (click)="addTicketType()"
                  >
                    <ion-label> <ion-icon name="ticket"></ion-icon><ion-icon name="add-outline"></ion-icon> </ion-label>
                  </ion-button>
                </ion-item>
                <ion-item>
                  <ion-label slot="end" text-wrap>
                    Limit to closed sub-statuses:
                    <ion-chip
                      mode="ios"
                      color="primary"
                      class="btn-small tag-chip"
                      slot="end"
                      *ngFor="let subStatus of postCloseSubStatuses; let i = index"
                      [title]="subStatus.name"
                      (click)="removePostCloseSubStatus(i)"
                    >
                      <ion-label>
                        {{ subStatus.name }}
                        <ion-icon name="close-outline"></ion-icon>
                      </ion-label>
                    </ion-chip>
                  </ion-label>
                  <ion-button
                    class="btn-small"
                    fill="clear"
                    style="--padding-start: 4px; margin: 5px; --padding-end: 4px"
                    slot="end"
                    (click)="addPostCloseSubStatus()"
                    [disabled]="!availableClosedSubStatuses?.length"
                  >
                    <ion-label> <ion-icon name="filter"></ion-icon><ion-icon name="add-outline"></ion-icon> </ion-label>
                  </ion-button>
                </ion-item>
                <ion-item>
                  <ion-label slot="end"> Send max once per conversation </ion-label>
                  <ion-checkbox style="margin: 5px" slot="end" [(ngModel)]="postCloseMaxOnce"> </ion-checkbox>
                </ion-item>
              </ng-container>
              <ion-item>
                <ion-label>
                  <h3>Default project</h3>
                </ion-label>
                <ion-chip mode="ios" class="select-chip select-outline" [outline]="true" (click)="setDefaultProject()">
                  <ion-label>
                    {{ defaultProject?.name ?? 'None' }}
                    <ion-icon name="chevron-down-outline" class="icon-padded-left"></ion-icon>
                  </ion-label>
                </ion-chip>
              </ion-item>
            </ion-list>
          </ion-card>

          <ion-card class="outline-card">
            <ion-card-title class="card-title">
              <h3>Stale Conversation Settings</h3>
              <div class="subtitle">Customize how stale conversations are handled</div>
            </ion-card-title>
            <ion-list>
              <ion-item>
                <ion-label>
                  <h3>Auto-close conversations</h3>
                  <p>Mark closed after a period of inactivity.</p>
                </ion-label>
                <ion-select
                  slot="end"
                  class="select-outline"
                  interface="popover"
                  [(ngModel)]="closeConversationsAfterHours"
                  [compareWith]="closeConversationsAfterHoursCompareFn"
                >
                  <ion-select-option [value]="null">Don't automatically close</ion-select-option>
                  <ion-select-option *ngFor="let option of conversationTimeoutOptions" [value]="option.value">
                    {{ option.label }}
                  </ion-select-option>
                </ion-select>
              </ion-item>
              <ion-item *ngIf="closeConversationsAfterHours">
                <ion-label>
                  <h3>When to close inactive tickets</h3>
                </ion-label>
                <ion-select
                  slot="end"
                  class="select-outline"
                  interface="popover"
                  [(ngModel)]="closeStaleConversationsMode"
                >
                  <ion-select-option value="always">Always</ion-select-option>
                  <ion-select-option value="if-latest-message-is-internal">
                    If agent sent last message
                  </ion-select-option>
                </ion-select>
              </ion-item>
              <ion-item>
                <ion-label>
                  <h3>Stale Intake Reminders</h3>
                  <p style="text-wrap: auto">
                    Remind users to close stale intakes explicitly after a certain period of inactivity.
                  </p>
                </ion-label>
                <ion-select
                  class="select-outline"
                  interface="popover"
                  [(ngModel)]="sendRemindersForStaleIntakesAfterMinutes"
                  [compareWith]="sendRemindersForStaleIntakesAfterMinutesCompareFn"
                  style="min-width: 100px; width: auto"
                >
                  <ion-select-option [value]="null">Do not send reminders</ion-select-option>
                  <ion-select-option
                    *ngFor="let option of staleIntakeReminderOptions"
                    [value]="option.value"
                    [disabled]="
                      closeConversationsAfterHours !== null && option.value / 60 >= closeConversationsAfterHours
                    "
                  >
                    {{ option.label }}
                  </ion-select-option>
                </ion-select>
              </ion-item>
            </ion-list>
          </ion-card>

          <ion-button
            expand="block"
            fill="clear"
            (click)="isShowingAdvanced = !isShowingAdvanced"
            style="margin: 20px 0"
          >
            <ion-icon
              [name]="isShowingAdvanced ? 'chevron-up-outline' : 'chevron-down-outline'"
              class="padded-icon"
            ></ion-icon>
            Show more advanced options
          </ion-button>
          <ion-card class="outline-card" *ngIf="isShowingAdvanced">
            <ion-card-header>
              <ion-card-title>
                <ion-label>Advanced configuration options</ion-label>
              </ion-card-title>
              <ion-card-subtitle>
                <ion-label>You probably don't need to change these 😉</ion-label>
              </ion-card-subtitle>
            </ion-card-header>
            <ion-list>
              <ion-item>
                <ion-checkbox [(ngModel)]="disableConversationsStartedByInternalUser"></ion-checkbox>
                <ion-label>
                  <h3>Do not create a conversation when a user replies to a thread created by an agent</h3>
                </ion-label>
              </ion-item>
              <ion-item>
                <ion-checkbox [(ngModel)]="doNotReopenConversationsOnReply"></ion-checkbox>
                <ion-label>
                  <h3>Do not re-open conversations when users reply</h3>
                  <p>Recommended: off</p>
                </ion-label>
              </ion-item>
              <ion-item>
                <ion-checkbox [(ngModel)]="autoSplittingEnabled"></ion-checkbox>
                <ion-label>
                  <h3>Auto-split conversations when new threads are branched off of a top-level message</h3>
                  <p>Recommended: on</p>
                </ion-label>
              </ion-item>
            </ion-list>
          </ion-card>
        </ion-col>
      </ion-row>
    </ion-grid>
  </div>
</ion-content>

<ion-footer>
  <ion-toolbar class="constrained-content">
    <ion-grid>
      <ion-row>
        <ion-col size="12" size-md="5"> </ion-col>
        <ion-col size="12" size-md="7">
          <ion-button color="success" (click)="save()">Save changes </ion-button>
        </ion-col>
      </ion-row>
    </ion-grid>
  </ion-toolbar>
</ion-footer>
